import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import mentorService from '../services/mentorService';
import { sociasApiService } from '../services/sociasApiService';
import '../styles/MentorManagement.css';

const MentorManagement = ({ createMode = false }) => {
    const navigate = useNavigate();
    const [mentors, setMentors] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [socias, setSocias] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showForm, setShowForm] = useState(createMode);
    const [currentMentor, setCurrentMentor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        sociaId: '',
        mentorNombre: '',
        mentorCedula: '',
        mentorObservacion: '',
        mentorSector: '',
        mentorEstado: 'Activo'
    });
    const [errors, setErrors] = useState({});

    // Cargar mentores y socias
    useEffect(() => {
        fetchMentors(page, pageSize);
        fetchSocias();
    }, []);

    useEffect(() => {
        // refetch when page or pageSize changes
        fetchMentors(page, pageSize);
    }, [page, pageSize]);

    const fetchMentors = async (p = 1, ps = 10) => {
        try {
            setLoading(true);
            const response = await mentorService.getAllMentores(p, ps);
            // The API may return either an array (no pagination) or a paginated object
            const resData = response?.data ?? response;
            if (Array.isArray(resData)) {
                setMentors(resData);
                setTotal(resData.length);
                setTotalPages(1);
            } else if (resData && Array.isArray(resData.data)) {
                setMentors(resData.data);
                setTotal(Number(resData.total) || 0);
                setTotalPages(Math.max(1, Math.ceil((Number(resData.total) || 0) / (Number(resData.pageSize) || ps))));
            } else {
                setMentors([]);
                setTotal(0);
                setTotalPages(1);
            }
        } catch (error) {
            console.error('Error al cargar mentores:', error);
            setMentors([]);
            setTotal(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    const fetchSocias = async () => {
        try {
            const response = await sociasApiService.getSocias();
            const sociasData = response.data || response;
            console.log('Socias cargadas:', sociasData); // Debug
            setSocias(Array.isArray(sociasData) ? sociasData : []);
        } catch (error) {
            console.error('Error al cargar socias:', error);
            setSocias([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Limpiar error cuando el usuario empiece a escribir
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSociaChange = (e) => {
        const sociaId = e.target.value;
        const sociaSeleccionada = socias.find(s => s.sociaId === sociaId);
        
        if (sociaSeleccionada) {
            // Límite de BD: 24 caracteres para nombre y sector
            const LIMIT = 24;
            const nombreCompleto = `${sociaSeleccionada.sociaNombre} ${sociaSeleccionada.sociaApellido}`;
            const nombreTrunc = nombreCompleto.toUpperCase().slice(0, LIMIT);
            const sectorTrunc = (sociaSeleccionada.sociaSector || '').toUpperCase().slice(0, LIMIT);
            setFormData(prev => ({
                ...prev,
                sociaId: sociaId,
                mentorNombre: nombreTrunc,
                mentorCedula: sociaSeleccionada.sociaCedula,
                mentorSector: sectorTrunc
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                sociaId: '',
                mentorNombre: '',
                mentorCedula: ''
            }));
        }
        
        // Limpiar errores
        if (errors.sociaId || errors.mentorNombre || errors.mentorCedula) {
            setErrors(prev => ({
                ...prev,
                sociaId: '',
                mentorNombre: '',
                mentorCedula: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.sociaId) newErrors.sociaId = 'Debe seleccionar una socia';
        if (!formData.mentorNombre.trim()) newErrors.mentorNombre = 'El nombre del mentor es requerido';
        if (!formData.mentorCedula.trim()) newErrors.mentorCedula = 'La cédula es requerida';
        if (!/^[0-9]{10}$/.test(formData.mentorCedula.trim())) newErrors.mentorCedula = 'La cédula debe tener 10 dígitos';
        if (!formData.mentorObservacion.trim()) newErrors.mentorObservacion = 'La observación es requerida';
        if (!formData.mentorSector.trim()) newErrors.mentorSector = 'El sector es requerido';
        if (!formData.mentorEstado) newErrors.mentorEstado = 'El estado es requerido';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Prevención extra: no permitir registrar una socia que ya es mentora
        if (!currentMentor && mentors.some(m => m.sociaId === formData.sociaId)) {
            setErrors(prev => ({ ...prev, sociaId: 'La socia seleccionada ya tiene rol de mentora' }));
            return;
        }
        if (!validateForm()) return;

        try {
            if (currentMentor) {
                // Actualizar mentor existente
                await mentorService.actualizarMentor(currentMentor.mentorId, formData);
                alert('Mentor actualizado exitosamente');
            } else {
                // Crear nuevo mentor
                await mentorService.registrarMentor(formData);
                alert('Mentor creado exitosamente');
            }
            
            await fetchMentors();
            
            // Si estamos en modo crear, navegar de vuelta a la lista después de crear
            if (createMode && !currentMentor) {
                navigate('/mentores');
            } else {
                resetForm();
            }
        } catch (error) {
            console.error('Error al guardar mentor:', error);
            alert('Error al guardar mentor: ' + (error.response?.data?.message || error.message));
        }
    };

    const handleEdit = (mentor) => {
        setCurrentMentor(mentor);
        setFormData({
            sociaId: mentor.sociaId,
            mentorNombre: mentor.mentorNombre,
            mentorCedula: mentor.mentorCedula,
            mentorObservacion: mentor.mentorObservacion,
            mentorSector: mentor.mentorSector,
            mentorEstado: mentor.mentorEstado
        });
        setShowForm(true);
    };

    const handleToggleStatus = async (mentor) => {
        const newStatus = mentor.mentorEstado === 'Activo' ? 'Inactivo' : 'Activo';
        const message = `¿Está seguro de ${newStatus === 'Activo' ? 'activar' : 'desactivar'} este mentor?`;

        if (window.confirm(message)) {
            try {
                await mentorService.actualizarMentor(mentor.mentorId, { mentorEstado: newStatus });
                alert('Estado del mentor actualizado');
                await fetchMentors();
            } catch (error) {
                console.error('Error al cambiar estado del mentor:', error);
                alert('Error al cambiar estado del mentor');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            sociaId: '',
            mentorNombre: '',
            mentorCedula: '',
            mentorObservacion: '',
            mentorSector: '',
            mentorEstado: 'Activo'
        });
        setCurrentMentor(null);
        setShowForm(false);
        setErrors({});
        
        // Si estamos en modo crear, navegamos de vuelta a la lista de mentores
        if (createMode) {
            navigate('/mentores');
        }
    };

    const filteredMentors = mentors.filter(mentor =>
        (mentor.mentorNombre?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (mentor.mentorCedula || '').includes(searchTerm) ||
        (mentor.mentorSector?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );

    // Socias disponibles para registrar como mentoras (excluye socias que ya tienen mentor)
    const mentorSociaIds = new Set((mentors || []).map(m => m.sociaId));
    const sociasDisponibles = (socias || []).filter(s => !mentorSociaIds.has(s.sociaId));

    return (
        <div className="mentor-management-container">
            <div className="mentor-management-header">
                <h1>Gestión de Mentores</h1>
                <p>Administre los mentores del sistema</p>
            </div>

            <div className="mentor-management-actions">
                <div className="search-section">
                    <div className="search-input">
                        <input
                            type="text"
                            placeholder="Buscar mentor por nombre, cédula o sector..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button type="button">
                            <i className="fas fa-search"></i>
                        </button>
                    </div>
                </div>
                <button 
                    className="btn-primary register-btn"
                    onClick={() => setShowForm(true)}
                >
                    <i className="fas fa-plus"></i> Nuevo Mentor
                </button>
            </div>
            
            <div className="mentors-table-container">
                <table className="mentors-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Mentor</th>
                            <th>Cédula</th>
                            <th>Sector</th>
                            <th>Observación</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                                    Cargando mentores...
                                </td>
                            </tr>
                        ) : filteredMentors.map(mentor => (
                            <tr key={mentor.mentorId}>
                                <td><span className="mentor-id">{mentor.mentorId}</span></td>
                                <td>
                                    <div className="mentor-name">
                                        <div className="mentor-icon">
                                            <i className="fas fa-user-tie"></i>
                                        </div>
                                        {mentor.mentorNombre}
                                    </div>
                                </td>
                                <td>{mentor.mentorCedula}</td>
                                <td>{mentor.mentorSector}</td>
                                <td>{mentor.mentorObservacion}</td>
                                <td>
                                    <span className={`status-badge ${mentor.mentorEstado?.toLowerCase()}`}>
                                        {mentor.mentorEstado}
                                    </span>
                                </td>
                                <td>
                                    <div className="actions">
                                        <button
                                            className="action-btn edit-btn"
                                            onClick={() => handleEdit(mentor)}
                                            title="Editar"
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button
                                            className={`action-btn ${mentor.mentorEstado === 'Activo' ? 'delete-btn' : 'activate-btn'}`}
                                            onClick={() => handleToggleStatus(mentor)}
                                            title={mentor.mentorEstado === 'Activo' ? 'Desactivar' : 'Activar'}
                                        >
                                            {mentor.mentorEstado === 'Activo' ? <i className="fas fa-ban"></i> : <i className="fas fa-check"></i>}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredMentors.length === 0 && (
                    <div className="table-footer">
                        No se encontraron mentores
                    </div>
                )}
            </div>
            
            <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                    Mostrando <strong>{filteredMentors.length}</strong> de <strong>{total || mentors.length}</strong> mentores
                </div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button className="btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</button>
                    <span> Página <strong>{page}</strong> de <strong>{totalPages}</strong> </span>
                    <button className="btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Siguiente</button>
                    <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>Por página:</span>
                        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                        </select>
                    </label>
                </div>
            </div>

            {showForm && (
                <div className="modal-overlay">
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <i className="fas fa-user-tie"></i>
                                {currentMentor ? 'Editar Mentor' : 'Nuevo Mentor'}
                            </h3>
                            <button className="modal-close" onClick={resetForm}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="mentor-form">
                            <div className="form-section">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="sociaId">Socia (*)</label>
                                        <select
                                            id="sociaId"
                                            name="sociaId"
                                            value={formData.sociaId}
                                            onChange={handleSociaChange}
                                            className={errors.sociaId ? 'error' : ''}
                                            disabled={!!currentMentor}
                                        >
                                            <option value="">Seleccione una socia</option>
                                            {sociasDisponibles.map(socia => (
                                                <option key={socia.sociaId} value={socia.sociaId}>
                                                    {formData.sociaId === socia.sociaId 
                                                        ? socia.sociaId 
                                                        : `${socia.sociaId} - ${socia.sociaNombre} ${socia.sociaApellido}`
                                                    }
                                                </option>
                                            ))}
                                        </select>
                                        {errors.sociaId && (
                                            <span className="error-message">{errors.sociaId}</span>
                                        )}
                                        {sociasDisponibles.length === 0 && (
                                            <small>No hay socias disponibles para registrar como mentoras.</small>
                                        )}
                                        {!!currentMentor && (
                                            <small>No se puede cambiar la socia de un mentor existente</small>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="mentorNombre">Nombre del Mentor (*)</label>
                                        <input
                                            type="text"
                                            id="mentorNombre"
                                            name="mentorNombre"
                                            value={formData.mentorNombre}
                                            onChange={handleInputChange}
                                            className={errors.mentorNombre ? 'error' : ''}
                                            maxLength="24"
                                            disabled
                                            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                        />
                                        {errors.mentorNombre && (
                                            <span className="error-message">{errors.mentorNombre}</span>
                                        )}
                                        <small>Se llena automáticamente al seleccionar una socia</small>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="mentorCedula">Cédula (*)</label>
                                        <input
                                            type="text"
                                            id="mentorCedula"
                                            name="mentorCedula"
                                            value={formData.mentorCedula}
                                            onChange={handleInputChange}
                                            className={errors.mentorCedula ? 'error' : ''}
                                            maxLength="10"
                                            disabled
                                            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                        />
                                        {errors.mentorCedula && (
                                            <span className="error-message">{errors.mentorCedula}</span>
                                        )}
                                        <small>Se llena automáticamente al seleccionar una socia</small>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="mentorSector">Sector (*)</label>
                                        <input
                                            type="text"
                                            id="mentorSector"
                                            name="mentorSector"
                                            value={formData.mentorSector}
                                            onChange={handleInputChange}
                                            className={errors.mentorSector ? 'error' : ''}
                                            maxLength="24"
                                            disabled
                                            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                        />
                                        {errors.mentorSector && (
                                            <span className="error-message">{errors.mentorSector}</span>
                                        )}
                                        <small>Se llena automáticamente al seleccionar una socia</small>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="mentorObservacion">Observación (*)</label>
                                        <textarea
                                            id="mentorObservacion"
                                            name="mentorObservacion"
                                            value={formData.mentorObservacion}
                                            onChange={handleInputChange}
                                            className={errors.mentorObservacion ? 'error' : ''}
                                            rows="4"
                                            maxLength="24"
                                        />
                                        {errors.mentorObservacion && (
                                            <span className="error-message">{errors.mentorObservacion}</span>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="mentorEstado">Estado (*)</label>
                                        <select
                                            id="mentorEstado"
                                            name="mentorEstado"
                                            value={formData.mentorEstado}
                                            onChange={handleInputChange}
                                            className={errors.mentorEstado ? 'error' : ''}
                                        >
                                            <option value="Activo">Activo</option>
                                            <option value="Inactivo">Inactivo</option>
                                        </select>
                                        {errors.mentorEstado && (
                                            <span className="error-message">{errors.mentorEstado}</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={resetForm}
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    {currentMentor ? 'Guardar Cambios' : 'Crear Mentor'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MentorManagement;